/**
 * An amount spelled out, the way an Indian commercial document spells it.
 *
 * WHY A DOCUMENT CARRIES THIS AT ALL. The figure and the words are a check on each other: a
 * digit lost or added in transit changes one and not the other, and the words are the version a
 * dispute falls back on. It is convention on every invoice and purchase order in the country.
 *
 * INDIAN GROUPING, NOT WESTERN. The scale goes crore (10^7), lakh (10^5), thousand, hundred —
 * so 2,89,800 is "Two Lakh, Eighty-Nine Thousand, Eight Hundred", never "Two Hundred
 * Eighty-Nine Thousand". A generic library would produce the second, which is why this is
 * written out rather than pulled in.
 *
 * Groups are comma-separated and the string ends "Rupees Only", matching the phrasing on the
 * documents this has to sit alongside.
 */
const ONES = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
    'Eighteen', 'Nineteen',
];
const TENS = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
];
/** 0-99. Hyphenated above twenty, as "Eighty-Nine" rather than "Eighty Nine". */
function underHundred(n) {
    if (n < 20)
        return ONES[n];
    const tens = TENS[Math.floor(n / 10)];
    const unit = n % 10;
    return unit ? `${tens}-${ONES[unit]}` : tens;
}
/** 0-999, as its own group: "Eight Hundred", "Eighty-Nine". */
function underThousand(n) {
    const hundreds = Math.floor(n / 100);
    const rest = n % 100;
    const parts = [];
    if (hundreds)
        parts.push(`${ONES[hundreds]} Hundred`);
    if (rest)
        parts.push(underHundred(rest));
    return parts.join(' ');
}
/**
 * The whole-rupee part, in Indian scale groups.
 *
 * Each group becomes its own comma-separated phrase, which is how the reference documents read:
 * "Two Lakh, Eighty-Nine Thousand, Eight Hundred".
 */
function rupeesInWords(rupees) {
    if (rupees === 0)
        return 'Zero';
    const groups = [];
    const crore = Math.floor(rupees / 10000000);
    const lakh = Math.floor((rupees % 10000000) / 100000);
    const thousand = Math.floor((rupees % 100000) / 1000);
    const rest = rupees % 1000;
    if (crore)
        groups.push(`${rupeesInWords(crore)} Crore`);
    if (lakh)
        groups.push(`${underThousand(lakh)} Lakh`);
    if (thousand)
        groups.push(`${underThousand(thousand)} Thousand`);
    if (rest)
        groups.push(underThousand(rest));
    return groups.join(', ');
}
/**
 * "INR Two Lakh, Eighty-Nine Thousand, Eight Hundred Rupees Only."
 *
 * PAISE ARE ONLY MENTIONED WHEN THERE ARE ANY. "and Fifty Paise" on an amount ending .00 reads
 * as an error, and most order totals are whole rupees.
 *
 * Negative amounts are not a thing an order total can be, so they are not special-cased; a
 * negative would simply read with a minus, which is visible enough to notice.
 */
export function amountInWords(amount, currency = 'INR') {
    const value = Number(amount) || 0;
    // Rounded to paise first, so 0.005 does not become "Zero Rupees and 1 Paise" from a float
    // that was never quite a paisa.
    const totalPaise = Math.round(value * 100);
    const rupees = Math.floor(totalPaise / 100);
    const paise = totalPaise % 100;
    const words = [`${currency} ${rupeesInWords(rupees)} Rupees`];
    if (paise)
        words.push(`and ${underHundred(paise)} Paise`);
    return `${words.join(' ')} Only.`;
}
//# sourceMappingURL=amountInWords.js.map